import { IoCloseSharp } from 'react-icons/io5';
import AppContext from './context/AppContext';
import { bool, number, oneOfType, string } from 'prop-types';
import BsImages from '../icons/BsImages';
import { CartContext } from './context/CartContext';
import getSymbolFromCurrency from 'currency-symbol-map';
import { Link } from 'react-router-dom';
import React, { useContext, useMemo, useState, useEffect, useCallback } from 'react';
import ResponsiveImage from './ResponsiveImage';
import '../styles/CartItem.css';
import constants from '../constants.json' assert { type: 'json' };
import useDynamicPath from '../hooks/useDynamicPath';
import { Form } from 'react-bootstrap';
import { FaChevronUp, FaChevronDown } from 'react-icons/fa';

const QUANTITY_UPDATE_DELAY_MS = 800;
const NON_NUMERIC_CHARS = /\D/g;

export default function CartItem({
    _isMobile,
    lineItemId,
    inventoryLevelId,
    price,
    quantity,
    _sku,
    title,
    availableQuantity,
    availableForSale,
    imageUrl,
    handle,
    bypassNegativeQuantity,
}) {
    const { appContext } = useContext(AppContext);
    const productPath = useDynamicPath({ pathName: constants.DYNAMIC_PATHS.PRODUCT_PATH });
    const appConfig = appContext.config.themeConfig;
    const {
        cartUpdating,
        addLineItem,
        setLineItem,
        delLineItem,
    } = useContext(CartContext);
    const [quantityUpdated, setQuantityUpdated] = useState(false);
    // Add hints that the inventory level is exhausted
    // https://app.clickup.com/t/86a1dkgna
    const quantityToAdd = useMemo(() => parseInt((bypassNegativeQuantity ? Infinity : availableQuantity) - quantity),
        [bypassNegativeQuantity, availableQuantity, quantity]);
    const [newQuantity, setNewQuantity] = useState(quantity);
    const handleQuantityChange = useCallback((e) => {
        const newQuantity = e.target.value.replace(NON_NUMERIC_CHARS, '');
        setNewQuantity(newQuantity ? parseFloat(newQuantity) : '');
        setQuantityUpdated(true);
    }, []);
    useEffect(() => {
        setNewQuantity(quantity);
    }, [quantity]);
    useEffect(() => {
        if (
            !isNaN(newQuantity)
            && (newQuantity <= availableQuantity && availableForSale)
            && newQuantity > 0
            && quantityUpdated
        ) {
            const timeout = setTimeout(() => {
                setLineItem(inventoryLevelId, newQuantity);
            }, QUANTITY_UPDATE_DELAY_MS);

            return () => clearTimeout(timeout);
        }
    }, [newQuantity, availableQuantity, availableForSale, inventoryLevelId, setLineItem, quantityUpdated]);
    const invalidQuantityMessage = useMemo(() => {
        if (newQuantity > availableQuantity) {
            return `Can't add more than ${availableQuantity}`;
        }
        if (!availableForSale) {
            return 'Item is not available for sale';
        }
        if (newQuantity === '') {
            return 'Invalid quantity';
        }
        if (newQuantity <= 0) {
            return 'Quantity must be greater than 0';
        }
        return 'Invalid quantity';
    }, [newQuantity, availableQuantity, availableForSale]);
    const isInvalid = useMemo(() => newQuantity > availableQuantity || !availableForSale || newQuantity <= 0 || isNaN(newQuantity),
        [newQuantity, availableQuantity, availableForSale]);
    const handleDeleteLineItem = useCallback(() => delLineItem(lineItemId), [lineItemId, delLineItem]);
    const handleIncreaseQuantity = useCallback(() => addLineItem(inventoryLevelId, 1), [inventoryLevelId]);
    const handleDecreaseQuantity = useCallback(() => setLineItem(inventoryLevelId, quantity - 1), [inventoryLevelId, quantity]);

    return (<div
        className={'cart-item'}>
        <div
            className={'cart-item--row'}>
            {imageUrl
                ? (<div
                    className={'cart-item--image'}><ResponsiveImage
                        imageUrl={imageUrl}
                        imageAlt={title || ''}
                        mobileSize={'100px'}
                        desktopSize={'100px'}></ResponsiveImage></div>)
                : (<div
                    className={'cart-item--image'}>
                    <BsImages
                        style={{ maxWidth: '80%' }} />
                </div>)}
            <div
                className={'cart-item--description'}>
                <Link
                    to={`${productPath[0].pathBeforeParams}/${handle}`}
                    className={'cart-item--title'}>{title}</Link>
                <div
                    className={'d-flex align-items-center justify-content-between w-100 pe-2'}>
                    <span
                        className={'cart-item--price'}>
                        {getSymbolFromCurrency(appConfig.storeCurrency)}{(price * quantity).toFixed(2)} {appConfig.storeCurrency}
                    </span>
                    <div
                        className={'d-flex align-items-center gap-1 justify-content-center'}>
                        <button
                            className={'cartitem--qty-button'}
                            style={{ ...(quantityToAdd <= 0 || cartUpdating) && ({ color: 'lightgray' }) }}
                            aria-label={'Add item quantity'}
                            onClick={handleIncreaseQuantity}
                            disabled={(quantityToAdd <= 0 || cartUpdating)}>
                            <FaChevronUp
                                size={'15px'} />
                        </button>
                        <Form.Control
                            className={'cartitem--qty-input'}
                            type={'text'}
                            value={newQuantity}
                            max={availableQuantity}
                            onChange={handleQuantityChange}
                            placeholder={'Enter quantity...'}
                            aria-label={'Quantity'}
                            isInvalid={isInvalid}
                        />
                        <button
                            className={'cartitem--qty-button'}
                            style={{ ...(quantity - 1 <= 0 || cartUpdating) && ({ color: 'lightgray' }) }}
                            aria-label={'Reduce item quantity'}
                            onClick={handleDecreaseQuantity}
                            disabled={(quantity - 1 <= 0 || cartUpdating)}>
                            <FaChevronDown
                                size={'15px'} />
                        </button>
                    </div>
                    {isInvalid &&
                    <div
                        style={{ color: '#dc3545' }}
                    >
                        {invalidQuantityMessage}
                    </div>
                    }
                </div>
            </div>

            <IoCloseSharp
                className={'cart-item--remove-button'}
                style={{ ...cartUpdating && ({ color: 'lightgray' }) }}
                aria-label={'Remove item'}
                onClick={handleDeleteLineItem}
                disabled={cartUpdating} />
        </div>

    </div>);
}

CartItem.propTypes = {
    _isMobile: bool,
    lineItemId: number,
    inventoryLevelId: number,
    price: oneOfType([number, string]),
    quantity: oneOfType([number, string]),
    _sku: string,
    title: string,
    availableQuantity: oneOfType([number, string]),
    availableForSale: oneOfType([number, bool]),
    imageUrl: string,
    handle: string,
    bypassNegativeQuantity: bool,
};